import { useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Clock, ListChecks, Package, Hammer, Wrench, MessageSquare, Ruler, ClipboardCheck, PenLine, Inbox, FileQuestion } from "lucide-react";
import { useDashboard, usePlan } from "@/lib/queries";
import { weekLabel, formatDateLong, SERVICE_STATUS_LABELS, INSTALL_STATUS_LABELS, PHASES, PHASE_LABELS } from "@/lib/format";
import { followUp, followUpHref, taskContext, isActionable } from "@/lib/followup";
import { PageHeader, ErrorBlock, Section, ActionLink } from "@/components/common/Blocks";
import { StateChip, toneForServiceStatus } from "@/components/common/Badges";
import { SplitPage, Enter } from "@/components/layout/SidePanel";
import { DeliveryPreview } from "@/components/delivery/DeliveryPreview";
import { KpiTile, SegmentBar, MiniColumns, PhaseBars, WeeklyLoadChart, CardTitle } from "@/components/dashboard/Widgets";

/* Each delivery appears once. Order inside a list follows this priority. */
const ORDER = ["ubehandlede_beskjeder", "klar_til_intern_kontroll", "endringer_under_behandling", "mangler_oppmaling", "nye_foresporsler", "venter_kundegodkjenning", "tilbud_venter_svar"];

/* Summary counts double as entries: each focuses the lists on one group. */
const FOCUS = {
  beskjeder: { group: "ubehandlede_beskjeder", label: "nye kundebeskjeder" },
  godkjenning: { group: "venter_kundegodkjenning", label: "venter på kundegodkjenning" },
  kontroll: { group: "klar_til_intern_kontroll", label: "klar til intern kontroll" },
  oppmaling: { group: "mangler_oppmaling", label: "mangler oppmåling" },
};

const splitRows = (fu) => {
  const seen = new Set();
  const work = [];
  const waiting = [];
  for (const g of ORDER) {
    for (const s of fu[g] || []) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      const f = followUp(s);
      (isActionable(f) ? work : waiting).push({ s, f });
    }
  }
  return { work, waiting };
};

/* What kind of work a task is – used for the KPI breakdown. */
const workType = (f) => {
  if (f.target === "melding") return "kundebeskjeder";
  if (f.title === "Oppmåling mangler") return "oppmåling";
  if (f.target === "frigivelse" || f.title === "Klar til intern kontroll") return "intern kontroll";
  if (f.target === "endring") return "endringer";
  if (f.target === "spesifikasjon") return "forespørsler";
  if (f.target === "service") return "restpunkter";
  return "annet";
};
const singular = { kundebeskjeder: "kundebeskjed", endringer: "endring", forespørsler: "forespørsel", restpunkter: "restpunkt" };
const TYPE_ICON = { kundebeskjeder: MessageSquare, oppmåling: Ruler, "intern kontroll": ClipboardCheck, endringer: PenLine, forespørsler: Inbox, restpunkter: Wrench, annet: FileQuestion };
const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return "God natt";
  if (h < 10) return "God morgen";
  if (h < 12) return "God formiddag";
  if (h < 17) return "God ettermiddag";
  return "God kveld";
};
const countText = (n, word) => `${n} ${n === 1 ? singular[word] || word : word}`;

const dayText = (iso, today) => {
  if (!iso) return "Dato ikke satt";
  if (iso === today) return "I dag";
  const d = new Date(iso);
  const t = d.toLocaleDateString("nb-NO", { weekday: "short", day: "numeric", month: "short" });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/* One task. The task is the headline; the project is context; one visible action. */
const TaskRow = ({ s, f, selected, onSelect, dense = false, index = 0 }) => {
  const actionable = isActionable(f);
  const Icon = actionable ? TYPE_ICON[workType(f)] || FileQuestion : Clock;
  const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(s); } };
  return (
    <Enter as="li" index={index} data-testid={`followup-row-${s.number}`}>
      <div
        role="button"
        tabIndex={0}
        className={`task-row grid-cols-[auto_minmax(0,1fr)_auto] items-center ${dense ? "py-2.5" : "py-3.5"}`}
        data-selected={selected}
        onClick={() => onSelect(s)}
        onKeyDown={onKey}
        aria-label={`${f.title} – ${s.project_name}`}
        data-testid={`open-${s.number}`}
      >
        <span className={`task-icon ${dense ? "!h-8 !w-8" : ""}`} aria-hidden="true"><Icon /></span>
        <div className="min-w-0">
          <p className={`${dense ? "text-[14.5px] font-medium" : "text-[15px] font-semibold"} leading-snug`} data-testid={`next-action-${s.number}`}>{f.title}</p>
          <p className="t-meta mt-1 truncate">{taskContext(s)}</p>
        </div>
        {actionable ? (
          <ActionLink to={followUpHref(s)} testId={`followup-action-${s.number}`} className="hidden sm:inline-flex" onClick={(e) => e.stopPropagation()}>{f.verb}</ActionLink>
        ) : (
          <span className="wait-text hidden sm:inline-flex"><Clock /> Venter</span>
        )}
      </div>
    </Enter>
  );
};

/* Skeleton that mirrors the final layout so nothing jumps. */
const OverviewSkeleton = () => (
  <div className="page" data-testid="overview-loading-skeleton" aria-busy="true">
    <div className="mb-6"><div className="skeleton mb-2 h-4 w-40" /><div className="skeleton h-8 w-44" /><div className="skeleton mt-2 h-4 w-64" /></div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-[136px] rounded-xl" />)}</div>
    <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-12"><div className="skeleton h-[280px] rounded-xl xl:col-span-7" /><div className="skeleton h-[280px] rounded-xl xl:col-span-5" /></div>
    <div className="mt-10 grid grid-cols-1 gap-x-12 gap-y-10 xl:grid-cols-12">
      <div className="space-y-3 xl:col-span-8">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-16" />)}</div>
      <div className="space-y-3 xl:col-span-4">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-12" />)}</div>
    </div>
  </div>
);

export default function OversiktPage() {
  const { data, isLoading, isError, refetch } = useDashboard();
  const plan = usePlan(0, 8);
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("valgt");
  const focus = params.get("fokus");
  const listRef = useRef(null);

  const setParam = useCallback((k, v) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (!v) n.delete(k); else n.set(k, v);
    return n;
  }, { replace: true }), [setParams]);
  const closePanel = useCallback(() => setParam("valgt", null), [setParam]);

  const fu = data?.follow_up || {};
  const { work, waiting } = useMemo(() => splitRows(fu), [fu]);
  const focusIds = useMemo(() => (focus && FOCUS[focus] ? new Set((fu[FOCUS[focus].group] || []).map((s) => s.id)) : null), [focus, fu]);
  const workRows = focusIds ? work.filter(({ s }) => focusIds.has(s.id)) : work;
  const waitingRows = focusIds ? waiting.filter(({ s }) => focusIds.has(s.id)) : waiting;

  const selected = useMemo(() => {
    if (!selectedId || !data) return null;
    const pool = [...Object.values(fu).flat(), ...(data.upcoming_installations || [])];
    return pool.find((s) => s.id === selectedId) || null;
  }, [selectedId, data, fu]);

  /* KPI readings – all derived from the data, nothing decorative. */
  const kpi = useMemo(() => {
    if (!data) return null;
    const byType = {};
    for (const { f } of work) byType[workType(f)] = (byType[workType(f)] || 0) + 1;
    const typeEntries = Object.entries(byType).sort((a, b) => b[1] - a[1]);
    const typeText = typeEntries.map(([k, n]) => countText(n, k)).join(" · ");
    const TYPE_COLOR = { kundebeskjeder: "var(--brand-navy)", "intern kontroll": "var(--brand-teal)", oppmåling: "var(--brand-chocolate)", endringer: "var(--brand-blue)", forespørsler: "var(--phase-tilbud)", restpunkter: "var(--kind-service)", annet: "var(--phase-foresporsel)" };
    const typeSegments = typeEntries.map(([k, n]) => ({ key: k, label: k, value: n, color: TYPE_COLOR[k] || TYPE_COLOR.annet }));
    const pc = data.phase_counts || {};
    const segments = PHASES.filter((p) => p !== "levert").map((p) => ({ key: p, label: PHASE_LABELS[p], value: pc[p] || 0, color: `var(--phase-${p})` }));
    const installs = data.upcoming_installations || [];
    const todayN = installs.filter((s) => s.installation?.planned_date === data.today).length;
    const horizon = (plan.data?.weeks || []).slice(0, 5);
    const perWeek = horizon.map((w) => ({ label: w.label, now: w.is_current, value: installs.filter((s) => s.installation?.planned_week === w.week).length }));
    const next = installs.find((s) => s.installation?.planned_date && s.installation.planned_date > data.today);
    const svc = data.open_service_cases || [];
    const planned = svc.filter((s) => s.status === "planlagt").length;
    const open = data.open_service_count - planned;
    const unassigned = svc.filter((s) => !s.assigned_to).length;
    const svcSegments = [
      { key: "planlagt", label: "Planlagt", value: planned, color: "var(--brand-teal)" },
      { key: "apen", label: "Åpen", value: Math.max(0, open), color: "var(--brand-chocolate)" },
    ];
    return { typeText, typeSegments, svcSegments, segments, todayN, perWeek, installsN: installs.length, next, planned, open, unassigned, pc };
  }, [data, work, plan.data]);

  if (isLoading) return <OverviewSkeleton />;
  if (isError) return <div className="page"><ErrorBlock onRetry={refetch} /></div>;

  const select = (s) => setParam("valgt", s.id);
  const scrollToList = () => { setParam("fokus", null); listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const installs = data.upcoming_installations || [];
  const today = installs.filter((s) => s.installation?.planned_date === data.today);
  const next = installs.filter((s) => s.installation?.planned_date !== data.today).slice(0, 5);
  const planWeeks = plan.data?.weeks || [];

  return (
    <SplitPage panel={selected ? <DeliveryPreview summary={selected} onClose={closePanel} /> : null} onClose={closePanel} title="Leveranse">
      <div className="page page-wash" data-testid="dashboard-page">
        <PageHeader eyebrow={`${greeting()}, Kari`} title="Oversikt" meta={`${formatDateLong(data.today)} · ${weekLabel(data.this_week)}`} />

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="kpi-row">
          <Enter index={0} className="flex"><KpiTile label="Til oppfølging" icon={ListChecks} value={work.length} accent onClick={scrollToList} testId="kpi-followup"
            sub={work.length ? <>{kpi.typeText}{waiting.length ? <> · <span className="whitespace-nowrap">{waiting.length} venter på andre</span></> : null}</> : "Ingenting krever behandling nå"}>
            {work.length > 0 && <SegmentBar segments={kpi.typeSegments} testId="kpi-followup-segments" />}
          </KpiTile></Enter>
          <Enter index={1} className="flex"><KpiTile label="Aktive leveranser" icon={Package} value={data.active_deliveries} to="/leveranser" testId="kpi-active"
            sub={<><strong>{kpi.pc.produksjon || 0}</strong> i produksjon · <strong>{kpi.pc.klargjoring || 0}</strong> i klargjøring · <strong>{kpi.pc.montering || 0}</strong> i montering</>}>
            <SegmentBar segments={kpi.segments} testId="kpi-phase-segments" />
          </KpiTile></Enter>
          <Enter index={2} className="flex"><KpiTile label="Montering neste 5 uker" icon={Hammer} value={kpi.installsN} to="/plan?type=montering" testId="kpi-installs"
            sub={kpi.todayN ? <><strong>{kpi.todayN}</strong> i dag{kpi.next ? ` · neste ${dayText(kpi.next.installation.planned_date, data.today).toLowerCase()}` : ""}</> : kpi.next ? `Neste ${dayText(kpi.next.installation.planned_date, data.today).toLowerCase()}` : "Ingen montering planlagt"}>
            {kpi.perWeek.length > 0 && <MiniColumns values={kpi.perWeek} color="var(--kind-montering)" testId="kpi-install-columns" />}
          </KpiTile></Enter>
          <Enter index={3} className="flex"><KpiTile label="Åpne serviceoppdrag" icon={Wrench} value={data.open_service_count} to="/service" testId="kpi-service"
            sub={data.open_service_count ? <><strong>{kpi.planned}</strong> planlagt · <strong>{kpi.open}</strong> {kpi.open === 1 ? "åpen" : "åpne"}{kpi.unassigned ? ` · ${kpi.unassigned} ikke tildelt` : ""}</> : "Ingen åpne oppdrag"}>
            {data.open_service_count > 0 && <SegmentBar segments={kpi.svcSegments} testId="kpi-service-segments" />}
          </KpiTile></Enter>
        </div>

        {/* Charts */}
        <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-12" data-testid="chart-row">
          <section className="chart-card xl:col-span-7" data-testid="weekly-load-card">
            <CardTitle title="Planlagt aktivitet de neste 8 ukene" meta="Antall aktiviteter per uke · manuell plan, ikke kapasitet" to="/plan" toLabel="Åpne plan" testId="chart-plan-link" />
            {plan.isLoading ? <div className="skeleton h-[190px] w-full" /> : plan.isError ? <p className="t-meta py-8 text-center">Kunne ikke hente planen.</p> : <WeeklyLoadChart weeks={planWeeks} items={plan.data.items} />}
          </section>
          <section className="chart-card xl:col-span-5" data-testid="phase-card">
            <CardTitle title="Aktive leveranser per fase" meta="Velg en fase for å se leveransene" to="/leveranser" toLabel="Alle leveranser" testId="chart-deliveries-link" />
            <PhaseBars counts={data.phase_counts} />
            <p className="t-meta mt-3 border-t pt-3">{data.phase_counts?.levert || 0} levert{(data.phase_counts?.levert || 0) === 1 ? "" : "e"} · {data.open_service_count} med åpne restpunkter</p>
          </section>
        </div>

        {/* Work lists + today's installation */}
        <div ref={listRef} className="mt-10 grid grid-cols-1 gap-x-12 gap-y-10 xl:grid-cols-12" style={{ scrollMarginTop: 24 }}>
          <div className="space-y-10 xl:col-span-8">
            <Section
              title="Til oppfølging"
              testId="followup-list"
              right={focusIds ? <span className="inline-flex items-center gap-2">Viser: {FOCUS[focus].label} <button type="button" className="link-quiet" onClick={() => setParam("fokus", null)} data-testid="focus-clear">Vis alle</button></span> : `${workRows.length}`}
            >
              {workRows.length === 0 ? (
                <p className="t-body py-5 text-muted-foreground" data-testid="followup-empty">{focusIds ? "Ingen oppgaver i dette utvalget." : "Ingenting krever behandling nå."}</p>
              ) : (
                <ul className="divide-y" data-testid="work-list">
                  {workRows.map(({ s, f }, i) => <TaskRow key={s.id} s={s} f={f} index={i} selected={s.id === selectedId} onSelect={select} />)}
                </ul>
              )}
            </Section>

            <Section title="Venter på andre" testId="waiting-list" right={`${waitingRows.length}`}>
              {waitingRows.length === 0 ? (
                <p className="t-body py-4 text-muted-foreground">{focusIds ? "Ingenting i dette utvalget." : "Ingenting venter hos kunde eller andre."}</p>
              ) : (
                <ul className="divide-y">
                  {waitingRows.map(({ s, f }, i) => <TaskRow key={s.id} s={s} f={f} index={i} selected={s.id === selectedId} onSelect={select} dense />)}
                </ul>
              )}
            </Section>
          </div>

          <div className="space-y-10 xl:col-span-4">
            <Section title="Montering" testId="upcoming-installations" right={<ActionLink to="/plan" testId="dashboard-plan-link" className="!text-[13px] !font-normal text-muted-foreground">Se plan</ActionLink>}>
              {installs.length === 0 ? (
                <p className="t-body py-4 text-muted-foreground">Ingen montering planlagt de neste ukene.</p>
              ) : (
                <div className="space-y-5">
                  <div data-testid="install-today">
                    <p className="t-label mb-1">I dag</p>
                    {today.length === 0 ? (
                      <p className="t-table py-2 text-muted-foreground">Ingen montering i dag.</p>
                    ) : (
                      <ul className="divide-y">
                        {today.map((s) => {
                          const inst = s.installation;
                          return (
                            <li key={s.id}>
                              <Link to={`/leveranser/${s.id}?tab=montering`} className="list-row list-row-link block !py-2.5" data-testid={`upcoming-${s.number}`}>
                                <p className="row-name truncate">{s.project_name}</p>
                                <p className="t-meta mt-0.5">{[inst.time_window, inst.installer || "Montør ikke satt", inst.status === "pagar" ? INSTALL_STATUS_LABELS.pagar.toLowerCase() : null].filter(Boolean).join(" · ")}</p>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  {next.length > 0 && (
                    <div data-testid="install-next">
                      <p className="t-label mb-1">Nærmeste</p>
                      <ul className="divide-y">
                        {next.map((s) => {
                          const inst = s.installation;
                          return (
                            <li key={s.id}>
                              <Link to={`/leveranser/${s.id}?tab=montering`} className="list-row list-row-link !py-2.5" data-testid={`upcoming-${s.number}`}>
                                <span className="w-[88px] shrink-0 text-[13.5px] tabular text-muted-foreground">{dayText(inst.planned_date, data.today)}</span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-[14.5px] font-medium leading-snug">{s.project_name}</span>
                                  <span className="t-meta block truncate">{[inst.installer || "Montør ikke satt", s.quantity > 1 ? `${s.quantity} trapper` : null].filter(Boolean).join(" · ")}</span>
                                </span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title="Serviceoppdrag" testId="open-service-cases" right={<ActionLink to="/service" testId="dashboard-service-link" className="!text-[13px] !font-normal text-muted-foreground">Alle ({data.open_service_count})</ActionLink>}>
              {data.open_service_cases.length ? (
                <ul className="divide-y">
                  {data.open_service_cases.slice(0, 4).map((s) => (
                    <li key={s.id}>
                      <Link to={`/service/${s.id}`} className="list-row list-row-link !py-2.5" data-testid={`service-link-${s.number}`}>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[14.5px] font-medium leading-snug">{s.title}</p>
                          <p className="t-meta truncate">{s.project_name}{s.planned_week ? ` · ${weekLabel(s.planned_week)}` : ""}{s.assigned_to ? ` · ${s.assigned_to}` : ""}</p>
                        </div>
                        <StateChip tone={toneForServiceStatus(s.status)}>{SERVICE_STATUS_LABELS[s.status]}</StateChip>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : <p className="t-body py-4 text-muted-foreground">Ingen åpne oppdrag.</p>}
            </Section>
          </div>
        </div>
      </div>
    </SplitPage>
  );
}
